/**
 * TopologyPage — interactive cross-vendor network map.
 *
 * Two view modes:
 *
 * 1. **Collapsed-sites** (default / no single-site filter): one summary
 *    node per site, packed into a grid. Each node shows site code +
 *    device count + health pill. Solves the 54k-device case: you see
 *    your fleet at site granularity, click into one to drill down.
 *
 * 2. **Single-site** (site filter narrows to exactly one match): every
 *    device for that site rendered. Inner layout depends on whether
 *    mesh data is available:
 *      - **Mesh-aware**: fetched on demand from /api/topology/site/{slug}/mesh.
 *        Root APs (Ethernet-uplinked) at the top, mesh leaves cascading
 *        out in concentric tiers by hop count. Edges = parent→child
 *        mesh links, colored by parent status. This is what was missing
 *        when QA saw "6 dormant" understate the cascade risk on ALOHX.
 *      - **Class-grouped fallback**: when the site has no wireless APs
 *        (or the mesh fetch failed), devices are grouped by class
 *        (appliances top row, switches middle, wireless bottom rows)
 *        in a regular grid.
 *
 * Auto-`fitView()` runs every time the layout changes so a filter
 * narrows + auto-zooms instead of leaving the user to hunt for the
 * cluster. Custom node renderer hides labels below zoom 0.55 to keep
 * dense sites legible.
 *
 * Hard cap (MAX_NODES = 200) still applies in single-site mode so a
 * tenant with a 500-AP convention center doesn't lock up the tab.
 */
import React, { createElement, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Handle,
  Position,
  useStore as useReactFlowStore,
  type Node,
  type Edge,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  AlertTriangle,
  Building2,
  Layers,
  RefreshCw,
  Search,
  Server,
  Wifi,
  Router,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import {
  CardGridSkeleton,
  PanelSkeleton,
  FormSkeleton,
  Skeleton,
} from "@/components/ui/skeleton";
import { useInventory } from "@/stores/inventory";
import { useTopologyMesh, type MeshTopology } from "@/stores/topology-mesh";
import {
  useTopologyLinkLayer,
  type LinkLayerTopology,
  type LinkLayerNode,
} from "@/stores/topology-linklayer";
import {
  useTopologyDeviceDetails,
  type DeviceDetails,
} from "@/stores/topology-device-details";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import type {
  InventoryDevice,
  DeviceStatus,
  DeviceVendor,
} from "@/api/schemas/devices";
import { useTheme } from "@/stores/theme";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { TopologyFlowCanvas } from "@/components/app/topology/topology-flow-canvas";

/* ============================================ */
/* ICON HELPER                                  */
/* ============================================ */

function iconFor(d: InventoryDevice): typeof Wifi {
  const m = (d.model ?? "").toUpperCase();
  if (d.vendor === "meraki") {
    if (m.startsWith("MR") || m.startsWith("CW")) return Wifi;
    if (m.startsWith("MS")) return Layers;
    if (m.startsWith("MX") || m.startsWith("Z")) return Router;
    return Server;
  }
  if (d.vendor === "aruba") {
    if (m.startsWith("IAP") || m.startsWith("AP") || /^\d{3}[A-Z]?$/.test(m))
      return Wifi;
    if (m.includes("CX") || m.startsWith("MS")) return Layers;
    if (m.startsWith("9") || m.startsWith("72") || m.startsWith("90"))
      return Router;
    return Wifi;
  }
  return Server;
}

/** Map a device's model prefix to a class bucket. Used by the
 *  class-grouped layout to decide row placement. */
function classOf(
  d: InventoryDevice,
): "appliance" | "switch" | "wireless" | "other" {
  const m = (d.model ?? "").toUpperCase();
  if (d.vendor === "meraki") {
    if (m.startsWith("MX") || m.startsWith("Z")) return "appliance";
    if (m.startsWith("MS")) return "switch";
    if (m.startsWith("MR") || m.startsWith("CW")) return "wireless";
    return "other";
  }
  if (d.vendor === "aruba") {
    if (m.startsWith("9") || m.startsWith("72")) return "appliance";
    if (m.includes("CX")) return "switch";
    if (m.startsWith("IAP") || m.startsWith("AP") || /^\d{3}[A-Z]?$/.test(m))
      return "wireless";
    return "other";
  }
  return "other";
}

/* ============================================ */
/* NODE RENDERERS                               */
/* ============================================ */

interface DeviceNodeData {
  device: InventoryDevice;
  /** Mesh hop count: 0=root, 1+=leaf. Used as a tiny chip in the corner
   *  so operators can spot mesh leaves vs roots at a glance. Undefined
   *  in collapsed mode or non-wireless devices. */
  meshHops?: number;
  [key: string]: unknown;
}

interface LinkEdgeData {
  sourcePort?: string | null;
  targetPort?: string | null;
  sourceProductType?: string | null;
  targetProductType?: string | null;
}

interface HoverCardState {
  nodeId: string;
  x: number;
  y: number;
}

/** Aurora-styled device tile. Label visibility is keyed off the global
 *  React Flow zoom — at low zoom the tile shrinks to a colored dot so
 *  dense sites stay legible. */
function DeviceNode({ data }: NodeProps<Node<DeviceNodeData>>) {
  const d = data.device;
  // Subscribe to React Flow's zoom (its internal store). Re-renders
  // when transform changes — cheap, scoped to nodes that mount this.
  const zoom = useReactFlowStore((s) => s.transform[2]);
  const dense = zoom < 0.55;

  // Dense mode: tiny colored disc, no labels. Click target still works.
  // Handles must still be present (even when invisible) so React Flow
  // can route smoothstep edges to/from this node.
  if (dense) {
    return (
      <div
        className={cn(
          "relative h-3 w-3 rounded-full ring-2 ring-bg-0/60",
          d.status === "online" && "bg-mint",
          d.status === "alerting" && "bg-peach",
          d.status === "offline" && "bg-rose",
          (d.status === "unknown" || !d.status) && "bg-fg-mute",
        )}
        title={`${d.name ?? d.serial} · ${d.model ?? "—"} · ${d.status ?? "unknown"}`}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative min-w-[150px] cursor-grab active:cursor-grabbing rounded-xl border bg-surface px-3 py-2.5 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)] hover:ring-2 hover:ring-violet/40",
        d.status === "online" && "border-mint/40",
        d.status === "alerting" && "border-peach/40",
        d.status === "offline" && "border-rose/40",
        (d.status === "unknown" || !d.status) && "border-line",
      )}
    >
      {/* Invisible handles — React Flow needs source/target anchors on
          every custom node or smoothstep edges have nowhere to route
          and render as a single dot at the node origin. */}
      <Handle
        type="target"
        position={Position.Left}
        className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
      />
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-md",
            d.status === "online" && "bg-mint/15 text-mint",
            d.status === "alerting" && "bg-peach/15 text-peach",
            d.status === "offline" && "bg-rose/15 text-rose",
            (d.status === "unknown" || !d.status) && "bg-line/30 text-fg-mute",
          )}
        >
          {createElement(iconFor(d), { size: 12 })}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[11px] font-semibold text-fg">
            {d.name ?? "(unnamed)"}
          </div>
          <div className="font-mono text-[9.5px] text-fg-mute">
            {d.model ?? "—"}
          </div>
        </div>
        {/* Mesh hop chip — only for wireless devices with mesh data. */}
        {typeof data.meshHops === "number" ? (
          <span
            className={cn(
              "rounded px-1 py-0.5 font-mono text-[9px]",
              data.meshHops === 0
                ? "bg-violet/20 text-violet"
                : "bg-peach/15 text-peach",
            )}
            title={
              data.meshHops === 0
                ? "Ethernet-uplinked (mesh root)"
                : `Mesh leaf · ${data.meshHops} hop(s) from root`
            }
          >
            {data.meshHops === 0 ? "root" : `h${data.meshHops}`}
          </span>
        ) : (
          <Badge variant="neutral" size="sm">
            {d.vendor}
          </Badge>
        )}
      </div>
    </div>
  );
}

/** Per-site rollup row used by the picker grid. Computed once from the
 *  filtered inventory; not rendered inside React Flow anymore — the
 *  picker is a plain DOM grid because the canvas isn't useful at
 *  1000+-site scale. */
interface SiteRollup {
  code: string;
  slug: string;
  count: number;
  online: number;
  alerting: number;
  offline: number;
  unknown: number;
  vendors: string[];
}

/** Compact endpoint tile — used by leaf-column nodes in the L2 view.
 *  Same data shape as DeviceNode but renders as a single short line
 *  (icon + truncated name) at ~20px tall. Lets us pack 50+ APs into a
 *  reasonable column height without the layout spilling off the
 *  canvas. Below zoom 0.55 still collapses to a dot, same as DeviceNode. */
function EndpointNode({ data }: NodeProps<Node<DeviceNodeData>>) {
  const d = data.device;
  const zoom = useReactFlowStore((s) => s.transform[2]);
  // Endpoint tile is already compact — only collapse to a dot at much
  // lower zooms (so the natural fit zoom for big sites still shows
  // labels). DeviceNode keeps the higher 0.55 threshold because the
  // wide labeled tile becomes unreadable past a point.
  const dense = zoom < 0.35;

  if (dense) {
    return (
      <div
        className={cn(
          "relative h-2.5 w-2.5 rounded-full ring-1 ring-bg-0/80",
          d.status === "online" && "bg-mint",
          d.status === "alerting" && "bg-peach",
          d.status === "offline" && "bg-rose",
          (d.status === "unknown" || !d.status) && "bg-fg-mute",
        )}
        title={`${d.name ?? d.serial} · ${d.model ?? "—"} · ${d.status ?? "unknown"}`}
      >
        <Handle
          type="target"
          position={Position.Left}
          className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
        />
        <Handle
          type="source"
          position={Position.Right}
          className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex h-[18px] min-w-[160px] cursor-grab active:cursor-grabbing items-center gap-1.5 rounded-md border bg-surface px-1.5 text-[10px] leading-none hover:ring-2 hover:ring-violet/40",
        d.status === "online" && "border-mint/30",
        d.status === "alerting" && "border-peach/30",
        d.status === "offline" && "border-rose/30",
        (d.status === "unknown" || !d.status) && "border-line",
      )}
      title={`${d.name ?? d.serial} · ${d.model ?? "—"} · ${d.status ?? "unknown"}`}
    >
      <Handle
        type="target"
        position={Position.Left}
        className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-1 !w-1 !border-0 !bg-transparent !opacity-0"
      />
      <span
        className={cn(
          "inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-sm",
          d.status === "online" && "text-mint",
          d.status === "alerting" && "text-peach",
          d.status === "offline" && "text-rose",
          (d.status === "unknown" || !d.status) && "text-fg-mute",
        )}
      >
        {createElement(iconFor(d), { size: 9 })}
      </span>
      <span className="truncate text-fg">{d.name ?? d.serial}</span>
      {typeof data.meshHops === "number" && data.meshHops > 0 ? (
        <span className="ml-auto shrink-0 font-mono text-[8px] text-peach">
          h{data.meshHops}
        </span>
      ) : null}
    </div>
  );
}

const nodeTypes = { device: DeviceNode, endpoint: EndpointNode };

/* ============================================ */
/* LAYOUT BUILDERS                              */
/* ============================================ */

/** Roll up the (filtered) inventory into per-site rows for the picker
 *  grid. Sites with offline devices float to the top so the worst
 *  state is one click away. */
function buildSiteIndex(devices: InventoryDevice[]): SiteRollup[] {
  const byCode = new Map<string, SiteRollup>();
  const vendorSets = new Map<string, Set<string>>();
  for (const d of devices) {
    const code = d.site ?? "unassigned";
    const slug = code.toLowerCase();
    if (!byCode.has(code)) {
      byCode.set(code, { code, slug, count: 0, online: 0, alerting: 0, offline: 0, unknown: 0, vendors: [] });
      vendorSets.set(code, new Set());
    }
    const b = byCode.get(code);
    if (!b) continue;
    b.count += 1;
    if (d.status === "online") b.online += 1;
    else if (d.status === "alerting") b.alerting += 1;
    else if (d.status === "offline") b.offline += 1;
    else b.unknown += 1;
    vendorSets.get(code)?.add(d.vendor);
  }
  for (const [code, b] of byCode) {
    b.vendors = [...(vendorSets.get(code) ?? new Set())];
  }
  return [...byCode.values()].sort((a, b) => {
    if (b.offline !== a.offline) return b.offline - a.offline;
    if (b.alerting !== a.alerting) return b.alerting - a.alerting;
    return a.code.localeCompare(b.code);
  });
}

/** Mesh-aware single-site layout: roots horizontally on top, leaves
 *  cascading down by hop count. Edges = parent→child mesh links,
 *  colored by parent status (offline=red, alerting=peach, otherwise
 *  violet). */
function buildMeshSiteView(
  devices: InventoryDevice[],
  mesh: MeshTopology,
): { nodes: Node[]; edges: Edge[] } {
  const bySerial = new Map(devices.map((d) => [d.serial, d]));
  // Bucket devices by hop count for layout. Non-wireless devices live
  // in their own pseudo-row above the wireless tiers.
  const wired = devices.filter((d) => classOf(d) !== "wireless");
  const wireless = devices.filter((d) => classOf(d) === "wireless");

  // Group wireless by hop count. Roots = hop 0. Anything missing from
  // the mesh response (e.g. a non-mesh-capable AP, or new device that
  // hasn't reported yet) is treated as hop 0 to keep it visible.
  const hopBuckets = new Map<number, InventoryDevice[]>();
  for (const d of wireless) {
    const h = mesh.hopCount[d.serial] ?? 0;
    const arr = hopBuckets.get(h) ?? [];
    arr.push(d);
    hopBuckets.set(h, arr);
  }
  const hops = [...hopBuckets.keys()].sort((a, b) => a - b);

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Wired row at y=0 — appliances on the left, switches to their right.
  // Each device gets ~180px of horizontal space.
  const wiredOrder = [...wired].sort((a, b) => {
    const ca = classOf(a) === "appliance" ? 0 : 1;
    const cb = classOf(b) === "appliance" ? 0 : 1;
    return ca - cb;
  });
  wiredOrder.forEach((d, i) => {
    nodes.push({
      id: d.serial,
      type: "device",
      position: { x: i * 180, y: 0 },
      data: { device: d },
    });
  });

  // Wireless tiers — each hop row sits ~160px below the previous.
  const wirelessTop = wiredOrder.length > 0 ? 180 : 0;
  hops.forEach((h, tierIdx) => {
    const bucket = hopBuckets.get(h)!;
    const y = wirelessTop + tierIdx * 160;
    // Center each tier — wider tiers (more APs) fan out more.
    const tierW = Math.max(1, bucket.length) * 180;
    const startX = -tierW / 2 + 90;
    bucket.forEach((d, i) => {
      nodes.push({
        id: d.serial,
        type: "device",
        position: { x: startX + i * 180, y },
        data: { device: d, meshHops: h },
      });
    });
  });

  // Edges: parent→child for every mesh link we have data for.
  for (const [childSerial, parentSerial] of Object.entries(mesh.parentOf)) {
    if (!bySerial.has(childSerial) || !bySerial.has(parentSerial)) continue;
    const parent = bySerial.get(parentSerial)!;
    edges.push({
      id: `mesh-${parentSerial}-${childSerial}`,
      source: parentSerial,
      target: childSerial,
      type: "default",
      animated: parent.status === "offline", // visual flag for cascade risk
      style: {
        stroke:
          parent.status === "offline"
            ? "rgb(var(--rose))"
            : parent.status === "alerting"
              ? "rgb(var(--peach))"
              : "rgb(var(--violet))",
        strokeOpacity: parent.status === "offline" ? 0.85 : 0.45,
        strokeWidth: parent.status === "offline" ? 1.8 : 1.2,
      },
    });
  }

  return { nodes, edges };
}

/** Class-grouped single-site fallback (no mesh data): appliances top,
 *  switches middle, wireless bottom — each in a grid sized to the row's
 *  device count. */
function buildClassSiteView(devices: InventoryDevice[]): {
  nodes: Node[];
  edges: Edge[];
} {
  const rows: Array<{ label: string; items: InventoryDevice[] }> = [
    {
      label: "appliances",
      items: devices.filter((d) => classOf(d) === "appliance"),
    },
    {
      label: "switches",
      items: devices.filter((d) => classOf(d) === "switch"),
    },
    {
      label: "wireless",
      items: devices.filter((d) => classOf(d) === "wireless"),
    },
    { label: "other", items: devices.filter((d) => classOf(d) === "other") },
  ].filter((r) => r.items.length > 0);

  const nodes: Node[] = [];
  let y = 0;
  for (const row of rows) {
    // Wireless rows often have many APs — wrap into multiple sub-rows
    // capped at 10 per line. Other classes are usually small enough to
    // fit on one line.
    const perLine = row.label === "wireless" ? 10 : 8;
    row.items.forEach((d, i) => {
      const subRow = Math.floor(i / perLine);
      const col = i % perLine;
      nodes.push({
        id: d.serial,
        type: "device",
        position: { x: col * 180, y: y + subRow * 90 },
        data: { device: d },
      });
    });
    const subRows = Math.ceil(row.items.length / perLine);
    y += subRows * 90 + 30;
  }
  return { nodes, edges: [] };
}

/** L2 link-layer layout — matches the shape Meraki's native topology
 *  page shows. Algorithm:
 *    1. Build undirected adjacency from the link list.
 *    2. Pick a root: prefer a Meraki appliance (MX/Z) if present;
 *       otherwise the highest-degree node; otherwise the first node.
 *    3. BFS from the root assigning each node a depth. Unreachable
 *       nodes (rare — usually means the network has multiple disjoint
 *       components) get their own BFS pass and land in subsequent
 *       row offsets.
 *    4. Group nodes by depth → vertical columns. Within each column,
 *       sort by parent index then by name so the layout is stable.
 *    5. Position: depth → x, intra-column index → y. Tall columns
 *       (many endpoints under one switch) get a smaller row pitch so
 *       the diagram stays compact horizontally.
 *
 *  `inventoryBySerial` lets us hydrate the L2 nodes with the inventory
 *  store's status data — Meraki's linkLayer call doesn't return live
 *  status, so we cross-reference by serial. Endpoints that exist only
 *  in linkLayer (discovered but not in inventory — phones, etc.) get
 *  a neutral "discovered" tile. */
function buildLinkLayerView(
  ll: LinkLayerTopology,
  inventoryBySerial: Map<string, InventoryDevice>,
  mesh: MeshTopology | undefined,
): { nodes: Node[]; edges: Edge[] } {
  if (ll.nodes.length === 0) return { nodes: [], edges: [] };

  // Adjacency. Use a Set to dedupe — Meraki occasionally returns the
  // same link twice (once per direction).
  const adj = new Map<string, Set<string>>();
  for (const n of ll.nodes) adj.set(n.id, new Set());
  for (const l of ll.links) {
    if (!adj.has(l.source) || !adj.has(l.target)) continue;
    adj.get(l.source)?.add(l.target);
    adj.get(l.target)?.add(l.source);
  }

  // Pick a root. Meraki marks the gateway/uplink with `root: true` —
  // use that directly. Fall back to (productType=appliance) → highest
  // degree → alphabetical for the rare case where Meraki returned no
  // root marker (e.g. an isolated network without an MX).
  const explicitRoot = ll.nodes.find((n) => n.isRoot);
  let rootId: string;
  if (explicitRoot) {
    rootId = explicitRoot.id;
  } else {
    const sortedByPriority = [...ll.nodes].sort((a, b) => {
      const aa = a.productType === "appliance" ? 0 : 1;
      const bb = b.productType === "appliance" ? 0 : 1;
      if (aa !== bb) return aa - bb;
      const da = adj.get(a.id)?.size ?? 0;
      const db = adj.get(b.id)?.size ?? 0;
      if (da !== db) return db - da;
      return a.name.localeCompare(b.name);
    });
    rootId = sortedByPriority[0]!.id;
  }

  // BFS layering. Tracks each node's parent so we can group children
  // under their parent within a column (stable ordering).
  const depth = new Map<string, number>();
  const parent = new Map<string, string | null>();
  const order: string[] = [];
  const queue: string[] = [rootId];
  depth.set(rootId, 0);
  parent.set(rootId, null);
  while (queue.length > 0) {
    const u = queue.shift()!;
    order.push(u);
    const du = depth.get(u)!;
    const neighbors = [...(adj.get(u) ?? [])].sort();
    for (const v of neighbors) {
      if (depth.has(v)) continue;
      depth.set(v, du + 1);
      parent.set(v, u);
      queue.push(v);
    }
  }
  // Catch any disconnected components — assign them an offset column
  // depth so they appear off to the side rather than overlapping.
  for (const n of ll.nodes) {
    if (!depth.has(n.id)) {
      depth.set(n.id, 0);
      parent.set(n.id, null);
      order.push(n.id);
    }
  }

  // Group by depth, sort within depth by (parent order, then name).
  const byDepth = new Map<number, LinkLayerNode[]>();
  const nodeById = new Map(ll.nodes.map((n) => [n.id, n]));
  for (const n of ll.nodes) {
    const d = depth.get(n.id) ?? 0;
    const arr = byDepth.get(d) ?? [];
    arr.push(n);
    byDepth.set(d, arr);
  }
  const orderIndex = new Map(order.map((id, i) => [id, i]));
  for (const arr of byDepth.values()) {
    arr.sort((a, b) => {
      // Group children under their parent: sort by parent's column index.
      const pa = parent.get(a.id);
      const pb = parent.get(b.id);
      const pad = pa ? (orderIndex.get(pa) ?? -1) : -1;
      const pbd = pb ? (orderIndex.get(pb) ?? -1) : -1;
      if (pad !== pbd) return pad - pbd;
      return a.name.localeCompare(b.name);
    });
  }

  // Layout. The L2 tree is left-to-right (depth 0 → maxDepth). Spacing
  // scales with density so large fan-out sites automatically breathe
  // more (wider columns, taller rows, earlier wrapping).
  //
  // Real-world driver: LAXBH has 460 APs at depth 3 and 11 discovered
  // hosts at depth 4. maxDepth=4 (small) but depth-3 is the huge fan
  // that needs both compact rendering and wrapping. Earlier versions
  // only wrapped the maxDepth column and missed this case → 460 nodes
  // stacked vertically off-canvas.
  const depths = [...byDepth.keys()].sort((a, b) => a - b);
  const maxDepth = depths.length === 0 ? 0 : depths[depths.length - 1]!;
  const maxColNodes = Math.max(
    ...depths.map((d) => byDepth.get(d)?.length ?? 0),
    1,
  );
  const density = Math.min(1, Math.max(0, (maxColNodes - 16) / 40));
  const COL_W = Math.round(320 + density * 140);
  const BRANCH_ROW_H = Math.round(86 + density * 28);
  const LEAF_ROW_H = Math.round(34 + density * 16);
  const COMPACT_THRESHOLD = Math.max(14, Math.round(24 - density * 8));
  const WRAP_THRESHOLD = Math.max(12, Math.round(24 - density * 10));

  // First pass: decide compact/wrap per depth + compute cumulative
  // x-offsets so a wrapped column at depth N pushes columns N+1, N+2
  // further right (otherwise children overlap their parent's sub-cols).
  type ColMeta = {
    compact: boolean;
    subCols: number;
    rowH: number;
    xOffset: number;
  };
  const meta = new Map<number, ColMeta>();
  let xCursor = 0;
  for (const d of depths) {
    const count = byDepth.get(d)!.length;
    const compact = d === maxDepth || count > COMPACT_THRESHOLD;
    const subCols =
      count > WRAP_THRESHOLD ? Math.ceil(count / WRAP_THRESHOLD) : 1;
    const rowH = compact ? LEAF_ROW_H : BRANCH_ROW_H;
    meta.set(d, { compact, subCols, rowH, xOffset: xCursor });
    xCursor += subCols;
  }

  const nodes: Node[] = [];
  for (const d of depths) {
    const col = byDepth.get(d)!;
    const m = meta.get(d)!;
    const perSub =
      m.subCols === 1 ? col.length : Math.ceil(col.length / m.subCols);
    const totalH = (perSub - 1) * m.rowH;
    col.forEach((n, i) => {
      const inv = n.serial ? inventoryBySerial.get(n.serial) : undefined;
      // Build the InventoryDevice for the renderer. Status priority:
      //   1. Meraki linkLayer's live `status` (most current)
      //   2. Inventory store status (cross-vendor, may be slightly stale)
      //   3. "unknown" — discovered host with no managed-device backing
      const liveStatus = (n.status ?? "").toLowerCase();
      const resolvedStatus: DeviceStatus =
        liveStatus === "online"
          ? "online"
          : liveStatus === "alerting"
            ? "alerting"
            : liveStatus === "offline"
              ? "offline"
              : liveStatus === "dormant"
                ? "unknown"
                : (inv?.status ?? "unknown");
      const device: InventoryDevice = inv
        ? { ...inv, status: resolvedStatus }
        : ({
            vendor: "meraki",
            serial: n.serial ?? n.id,
            name: n.name,
            model: n.model || "—",
            status: resolvedStatus,
            site: null,
            groupOrNetwork: null,
            ipAddress: n.ipv4 ?? null,
            mac: n.mac ?? undefined,
          } as InventoryDevice);
      const meshHops = mesh?.hopCount[device.serial];
      // For wrapped columns, distribute items into sub-columns. Item i
      // lives in sub-column floor(i / perSub) at row (i % perSub).
      // The depth's x-offset (from the cumulative pass above) pushes
      // it past any wrapping done by earlier depths so wide columns
      // don't bleed into their children's space.
      const subColIdx = m.subCols === 1 ? 0 : Math.floor(i / perSub);
      const rowInSub = m.subCols === 1 ? i : i % perSub;
      const x = (m.xOffset + subColIdx) * COL_W;
      const y = rowInSub * m.rowH - totalH / 2;
      nodes.push({
        id: n.id,
        // Compact columns use the endpoint renderer (single-line tile).
        // Branch columns (e.g., MX + core + access switches) keep the
        // full DeviceNode tile with two-line label + icon.
        type: m.compact ? "endpoint" : "device",
        position: { x, y },
        data: { device, meshHops },
      });
    });
  }

  // Second pass: collision smoothing per rendered column. Dense sites
  // can still produce near-overlaps after wrapping; this deterministic
  // pass enforces a minimum vertical gap and recenters each column.
  const byX = new Map<number, Node[]>();
  for (const n of nodes) {
    const arr = byX.get(n.position.x) ?? [];
    arr.push(n);
    byX.set(n.position.x, arr);
  }
  for (const col of byX.values()) {
    const sorted = [...col].sort((a, b) => a.position.y - b.position.y);
    const hasEndpoints = sorted.some((n) => n.type === "endpoint");
    const colDensityBoost = Math.min(1, Math.max(0, (sorted.length - 18) / 36));
    const minGap = hasEndpoints
      ? Math.round(30 + density * 12 + colDensityBoost * 10)
      : Math.round(72 + density * 20 + colDensityBoost * 14);
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1]!;
      const cur = sorted[i]!;
      if (cur.position.y - prev.position.y < minGap) {
        cur.position.y = prev.position.y + minGap;
      }
    }
    const meanY =
      sorted.reduce((sum, n) => sum + n.position.y, 0) /
      Math.max(sorted.length, 1);
    sorted.forEach((n) => {
      n.position.y -= meanY;
    });
  }

  // Edges. Two issues to handle:
  //   1) Meraki's link list is undirected (no parent/child); our BFS
  //      knows which side is the upstream. Normalize so `source` is
  //      always the lower-depth (upstream) node and `target` is the
  //      higher-depth (downstream) node. With handles set as
  //      source=Right + target=Left on every node, this produces clean
  //      left-to-right smoothstep routing.
  //   2) Color = worst-status of either endpoint, so an offline AP
  //      visibly degrades the whole branch leading to it.
  const edges: Edge[] = ll.links.map((l, i) => {
    const da = depth.get(l.source) ?? 0;
    const db = depth.get(l.target) ?? 0;
    const [src, tgt] = da <= db ? [l.source, l.target] : [l.target, l.source];
    const resolveStatus = (id: string): string => {
      const node = nodeById.get(id);
      if (node?.status) return node.status.toLowerCase();
      if (node?.serial) {
        const inv = inventoryBySerial.get(node.serial);
        if (inv?.status) return inv.status;
      }
      return "unknown";
    };
    const sevRank = (s: string) =>
      s === "offline" ? 3 : s === "alerting" ? 2 : s === "online" ? 1 : 0;
    const worst = Math.max(
      sevRank(resolveStatus(src)),
      sevRank(resolveStatus(tgt)),
    );
    return {
      id: `ll-${src}-${tgt}-${i}`,
      source: src,
      target: tgt,
      type: "smoothstep",
      data: {
        sourcePort: da <= db ? l.sourcePort : l.targetPort,
        targetPort: da <= db ? l.targetPort : l.sourcePort,
        sourceProductType: nodeById.get(src)?.productType ?? null,
        targetProductType: nodeById.get(tgt)?.productType ?? null,
      } satisfies LinkEdgeData,
      animated: worst === 3,
      style: {
        stroke:
          worst === 3
            ? "rgb(var(--rose))"
            : worst === 2
              ? "rgb(var(--peach))"
              : "rgb(var(--violet))",
        strokeOpacity: worst === 3 ? 0.9 : worst === 2 ? 0.7 : 0.6,
        strokeWidth: worst === 3 ? 2 : 1.5,
      },
    };
  });

  return { nodes, edges };
}

/* ============================================ */
/* PAGE                                         */
/* ============================================ */

/** Hard cap on devices rendered in single-site mode. The 200-cap was
 *  conservative for the global view (now collapsed by default); for a
 *  single site even a 500-AP convention center is rare so we still
 *  cap at 200 to keep the renderer responsive. */
const MAX_NODES_PER_SITE = 200;
const ALL_VENDORS: DeviceVendor[] = ["meraki", "aruba", "shasta"];
const ALL_STATUSES: DeviceStatus[] = [
  "online",
  "alerting",
  "offline",
  "unknown",
];

function useTopologyPage(flowHostRef: React.RefObject<HTMLDivElement | null>) {
  const { data, status, error, load } = useInventory();
  const themeMode = useTheme((s) => s.resolved);
  const isInitialLoading = status === "loading" && data.length === 0;
  useEffect(() => {
    void load();
  }, [load]);

  const [vendors, setVendors] = useState<DeviceVendor[]>([]);
  const [statuses, setStatuses] = useState<DeviceStatus[]>([]);
  const [siteQueryRaw, setSiteQueryRaw] = useState("");
  // When a user clicks a node on the L2 canvas, we swap the right-side
  // summary panel for a device-details view (ports/clients). Selection
  // is per-device-serial; clearing it returns to the summary.
  const [selectedSerial, setSelectedSerial] = useState<string | null>(null);
  // Edge-highlight focus. Hover sets `hoverNodeId` transiently; clicking
  // a node pins `pinnedNodeId`. When either is set, edges incident to
  // that node brighten and all others dim — that's how you trace
  // "what's connected to what" in a dense tree.
  const [hoverNodeId, setHoverNodeId] = useState<string | null>(null);
  const [pinnedNodeId, setPinnedNodeId] = useState<string | null>(null);
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [hoverCard, setHoverCard] = useState<HoverCardState | null>(null);
  // L2 default hides wireless devices — at large hotel sites (LAXBH has
  // 460 APs) including them turns the canvas into unreadable noise. The
  // L2 tree's value is the wired path (MX → core → access). Operators
  // who want APs in the picture toggle them on; the mesh view is the
  // proper place for AP-to-AP relationships anyway.
  const [showWireless, setShowWireless] = useState(false);

  // Clears the per-site selection state whenever the user changes filters.
  // A serial or node id from one site is meaningless on another.
  function clearFocusState() {
    setSelectedSerial(null);
    setPinnedNodeId(null);
    setHoverNodeId(null);
    setHoveredEdgeId(null);
    setHoverCard(null);
  }

  function setSiteQuery(q: string) {
    setSiteQueryRaw(q);
    clearFocusState();
  }

  function toggleVendor(v: DeviceVendor) {
    setVendors((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
    clearFocusState();
  }
  function toggleStatus(s: DeviceStatus) {
    setStatuses((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    clearFocusState();
  }

  // Apply vendor + status filters first; the site filter is a contains-match
  // on the device's site field. We don't cap here — caps apply per-mode below.
  const filtered = useMemo(() => {
    const vendorSet = new Set(vendors);
    const statusSet = new Set(statuses);
    const q = siteQueryRaw.trim().toLowerCase();
    return data.filter(
      (d) =>
        (vendors.length === 0 || vendorSet.has(d.vendor)) &&
        (statuses.length === 0 || statusSet.has(d.status)) &&
        (!q || (d.site ?? "").toLowerCase().includes(q)),
    );
  }, [data, vendors, statuses, siteQueryRaw]);

  // Determine view mode. Single-site mode kicks in when the user's
  // filter narrows the visible sites to exactly one. Otherwise the
  // page renders a structured site-picker grid instead of a canvas —
  // the previous "all sites on a canvas" view was unreadable at
  // 1000+ sites (everything collapsed to dots, no labels). The canvas
  // is reserved for the per-site drill-down where it's actually
  // useful (mesh hierarchy + device class layout).
  const sitesInView = useMemo(() => {
    return Array.from(new Set(filtered.map((d) => d.site ?? "unassigned")));
  }, [filtered]);
  const singleSite = sitesInView.length === 1 ? sitesInView[0]! : null;
  const isPickerView = singleSite === null;

  // Mesh + L2 link-layer data — both are per-site, both fetched lazily
  // in single-site mode. L2 is the primary layout source (matches the
  // wired tree Meraki itself draws); mesh layers on top for wireless
  // hop chips + the cascade-risk card. Slug derivation matches the
  // api-shim's slug_from_code().
  const meshStore = useTopologyMesh();
  const linkLayerStore = useTopologyLinkLayer();
  const { load: linkLayerLoad } = linkLayerStore;
  const { load: meshLoad } = meshStore;
  const singleSiteSlug = useMemo(() => {
    if (!singleSite) return null;
    const tail = singleSite.includes("-")
      ? singleSite.split("-").pop()!
      : singleSite;
    return tail.trim().slice(-5).toLowerCase();
  }, [singleSite]);
  const hasWireless = useMemo(() => {
    if (!singleSite) return false;
    return filtered.some((d) => classOf(d) === "wireless");
  }, [singleSite, filtered]);

  useEffect(() => {
    if (!singleSiteSlug) return;
    void linkLayerLoad(singleSiteSlug);
    // meshLoad returns 404 for wired-only sites; store handles it gracefully.
    void meshLoad(singleSiteSlug);
  }, [singleSiteSlug, linkLayerLoad, meshLoad]);
  const mesh = singleSiteSlug ? meshStore.bySlug[singleSiteSlug] : undefined;
  const meshLoading = singleSiteSlug
    ? !!meshStore.loading[singleSiteSlug]
    : false;
  const linkLayerRaw = singleSiteSlug
    ? linkLayerStore.bySlug[singleSiteSlug]
    : undefined;
  const linkLayerLoading = singleSiteSlug
    ? !!linkLayerStore.loading[singleSiteSlug]
    : false;
  // Filtered L2 — drops wireless nodes (and any link touching them)
  // when showWireless is false. This is what gives the L2 tree its
  // clean "wired only" default view on big hotel sites. When toggled
  // on we pass the raw response through unchanged.
  const linkLayer = useMemo<LinkLayerTopology | undefined>(() => {
    if (!linkLayerRaw) return undefined;
    if (showWireless) return linkLayerRaw;
    const wirelessIds = new Set(
      linkLayerRaw.nodes.flatMap((n) =>
        n.productType === "wireless" ? [n.id] : [],
      ),
    );
    return {
      nodes: linkLayerRaw.nodes.filter((n) => !wirelessIds.has(n.id)),
      links: linkLayerRaw.links.filter(
        (l) => !wirelessIds.has(l.source) && !wirelessIds.has(l.target),
      ),
    };
  }, [linkLayerRaw, showWireless]);
  // Counters used by the toggle button text.
  const wirelessCount = useMemo(() => {
    if (!linkLayerRaw) return 0;
    return linkLayerRaw.nodes.filter((n) => n.productType === "wireless")
      .length;
  }, [linkLayerRaw]);

  // Device-details panel — fetched on demand when the user clicks a
  // node. The store dedupes concurrent fetches and caches by serial.
  const deviceDetailsStore = useTopologyDeviceDetails();
  const selectedDetails = selectedSerial
    ? deviceDetailsStore.bySerial[selectedSerial]
    : undefined;
  const selectedDetailsLoading = selectedSerial
    ? !!deviceDetailsStore.loading[selectedSerial]
    : false;
  const selectedDetailsError = selectedSerial
    ? deviceDetailsStore.errors[selectedSerial]
    : undefined;
  // Inventory lookup by serial — used by buildLinkLayerView to hydrate
  // L2 nodes with live status from the inventory store. Built outside
  // the layout memo so we don't rebuild the Map for every layout pass.
  const inventoryBySerial = useMemo(() => {
    const m = new Map<string, InventoryDevice>();
    for (const d of filtered) m.set(d.serial, d);
    return m;
  }, [filtered]);

  // In picker view we don't build a React Flow graph at all — the
  // picker is a DOM grid. In single-site mode we prefer the L2
  // link-layer view (matches Meraki's native topology page), falling
  // back to mesh-only or class-grouped if L2 data isn't available.
  // L2 mode bypasses MAX_NODES_PER_SITE because Meraki's linkLayer
  // response is already a complete, bounded tree per network — we
  // shouldn't truncate it. The cap only applies to inventory-driven
  // layouts where untruncated could mean tens of thousands of rows.
  const { nodes, edges, mode } = useMemo(() => {
    if (isPickerView) {
      return {
        nodes: [] as Node[],
        edges: [] as Edge[],
        mode: "picker" as const,
      };
    }
    if (linkLayer && linkLayer.nodes.length > 0) {
      return {
        ...buildLinkLayerView(linkLayer, inventoryBySerial, mesh),
        mode: "l2" as const,
      };
    }
    const capped = filtered.slice(0, MAX_NODES_PER_SITE);
    if (
      mesh &&
      (mesh.roots.length > 0 || Object.keys(mesh.parentOf).length > 0)
    ) {
      return { ...buildMeshSiteView(capped, mesh), mode: "mesh" as const };
    }
    return { ...buildClassSiteView(capped), mode: "class" as const };
  }, [isPickerView, filtered, linkLayer, mesh, inventoryBySerial]);

  // Edge highlighting. When a node is hovered or pinned, edges touching
  // it brighten and the rest dim to ~8% opacity so the operator can
  // trace a single device's links through a dense tree. Applied as a
  // post-process on the base edges so hovering doesn't rebuild the
  // (expensive) layout memo. Hover takes priority over pin so sweeping
  // the canvas feels responsive.
  const focusNodeId = hoverNodeId ?? pinnedNodeId;
  const l2NodesById = useMemo(() => {
    const m = new Map<string, LinkLayerNode>();
    if (!linkLayerRaw) return m;
    for (const n of linkLayerRaw.nodes) m.set(n.id, n);
    return m;
  }, [linkLayerRaw]);

  const renderedNodeById = useMemo(() => {
    const m = new Map<string, Node<DeviceNodeData>>();
    for (const n of nodes) {
      const device = (n.data as DeviceNodeData | undefined)?.device;
      if (!device) continue;
      m.set(n.id, n as Node<DeviceNodeData>);
    }
    return m;
  }, [nodes]);

  const hoverCardNode = hoverCard
    ? renderedNodeById.get(hoverCard.nodeId)
    : null;
  const hoverCardDevice = hoverCardNode
    ? (hoverCardNode.data as DeviceNodeData).device
    : null;
  const hoverCardL2Node = hoverCardDevice
    ? l2NodesById.get(hoverCardNode!.id)
    : undefined;

  const edgeHasFocus = Boolean(focusNodeId || hoveredEdgeId);
  const styledEdges = useMemo<Edge[]>(() => {
    return edges.map((e) => {
      const data = (e.data as LinkEdgeData | undefined) ?? undefined;
      const incident = e.source === focusNodeId || e.target === focusNodeId;
      const sourceSwitchFocused =
        !!focusNodeId &&
        e.source === focusNodeId &&
        data?.sourceProductType === "switch";
      const isHoveredEdge = hoveredEdgeId === e.id;
      const shouldShowPort = isHoveredEdge || sourceSwitchFocused;
      const portLabel = shouldShowPort
        ? data?.sourcePort || data?.targetPort || null
        : null;
      const base = e.style ?? {};
      const strokeOpacity = focusNodeId
        ? incident
          ? 0.95
          : 0.07
        : isHoveredEdge
          ? 0.95
          : base.strokeOpacity;
      const strokeWidth = focusNodeId
        ? incident
          ? 2.5
          : 1
        : isHoveredEdge
          ? 2.5
          : base.strokeWidth;
      return {
        ...e,
        label: portLabel ?? undefined,
        labelStyle: portLabel
          ? {
              fontSize: 10,
              fontFamily: "var(--font-mono, ui-monospace)",
              fill: "rgb(var(--fg))",
            }
          : undefined,
        labelBgPadding: portLabel ? [5, 3] : undefined,
        labelBgBorderRadius: portLabel ? 5 : undefined,
        labelBgStyle: portLabel
          ? {
              fill: "rgb(var(--bg-0))",
              fillOpacity: 0.9,
              stroke: "rgb(var(--line))",
              strokeOpacity: 0.85,
            }
          : undefined,
        zIndex: isHoveredEdge ? 14 : incident ? 10 : 0,
        animated:
          isHoveredEdge || incident
            ? e.animated
            : edgeHasFocus
              ? false
              : e.animated,
        style: {
          ...base,
          strokeOpacity,
          strokeWidth,
        },
      };
    });
  }, [edges, focusNodeId, hoveredEdgeId, edgeHasFocus]);

  function setHoverCardFromEvent(event: React.MouseEvent, nodeId: string) {
    const hostRect = flowHostRef.current?.getBoundingClientRect();
    if (!hostRect) return;
    const CARD_W = 300;
    const CARD_H = 260;
    const PAD = 12;
    const desiredX = event.clientX - hostRect.left + 14;
    const desiredY = event.clientY - hostRect.top + 14;
    const clampedX = Math.max(
      PAD,
      Math.min(desiredX, hostRect.width - CARD_W - PAD),
    );
    const clampedY = Math.max(
      PAD,
      Math.min(desiredY, hostRect.height - CARD_H - PAD),
    );
    setHoverCard({
      nodeId,
      x: clampedX,
      y: clampedY,
    });
  }

  // Picker-mode site list. Capped to keep the page snappy at 1000+
  // sites; the search box filters further.
  const siteIndex = useMemo(() => buildSiteIndex(filtered), [filtered]);
  const PICKER_CAP = 60;
  const visibleSites = useMemo(
    () => siteIndex.slice(0, PICKER_CAP),
    [siteIndex],
  );
  const pickerOverflow = Math.max(0, siteIndex.length - PICKER_CAP);

  // The MAX_NODES_PER_SITE overflow indicator only matters in the
  // mesh / class fallback paths. L2 mode renders every node Meraki
  // returns and shouldn't show "X more devices match" — that text
  // confuses operators into thinking we hid something.
  const overflow =
    isPickerView || mode === "l2"
      ? 0
      : Math.max(0, filtered.length - MAX_NODES_PER_SITE);

  const vendorCounts = useMemo(() => {
    const c: Record<DeviceVendor, number> = { meraki: 0, aruba: 0, shasta: 0 };
    for (const d of data) c[d.vendor] = (c[d.vendor] ?? 0) + 1;
    return c;
  }, [data]);

  return {
    data, status, error, load,
    themeMode, isInitialLoading,
    vendors, setVendors, statuses, setStatuses, siteQuery: siteQueryRaw, setSiteQuery,
    selectedSerial, setSelectedSerial,
    hoverNodeId, setHoverNodeId, pinnedNodeId, setPinnedNodeId,
    hoveredEdgeId, setHoveredEdgeId, hoverCard, setHoverCard,
    showWireless, setShowWireless,
    toggleVendor, toggleStatus,
    filtered, sitesInView, singleSite, isPickerView,
    meshStore, linkLayerStore, singleSiteSlug, hasWireless,
    mesh, meshLoading, linkLayerRaw, linkLayerLoading, linkLayer,
    wirelessCount, deviceDetailsStore,
    selectedDetails, selectedDetailsLoading, selectedDetailsError,
    inventoryBySerial, nodes, edges, mode,
    hoverCardDevice, hoverCardL2Node,
    styledEdges, setHoverCardFromEvent,
    siteIndex, PICKER_CAP: 60, visibleSites, pickerOverflow,
    overflow, vendorCounts,
  };
}

type TopologyPageState = ReturnType<typeof useTopologyPage>;

function TopologyPickerView({ t }: { t: TopologyPageState }) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Pick a site to inspect</CardTitle>
              <CardSub className="mt-1">
                Problem sites first.{" "}
                {t.pickerOverflow > 0
                  ? `Showing top ${t.PICKER_CAP} of ${t.siteIndex.length} — refine the search above to find others.`
                  : `${t.siteIndex.length} sites match.`}
              </CardSub>
            </div>
          </div>
          {t.visibleSites.length === 0 ? (
            t.status === "loading" ? (
              <CardGridSkeleton cards={6} className="lg:grid-cols-3" />
            ) : (
              <div className="rounded-xl border border-line bg-surface-2/40 px-3 py-8 text-center text-xs text-fg-mute">
                {t.data.length === 0 ? "No devices to map yet — add a vendor key." : "No sites match your filters."}
              </div>
            )
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {t.visibleSites.map((s) => {
                const healthy = s.online === s.count;
                const hasOffline = s.offline > 0;
                const hasAlerting = s.alerting > 0;
                return (
                  <button key={s.code} type="button" onClick={() => t.setSiteQuery(s.code)}
                    className={cn(
                      "flex flex-col gap-1.5 rounded-xl border bg-surface px-3 py-2.5 text-left transition-colors hover:bg-surface-2",
                      hasOffline ? "border-rose/40" : hasAlerting ? "border-peach/40" : healthy ? "border-mint/40" : "border-line",
                    )}
                    title={`Open ${s.code} topology`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-[12px] font-semibold text-fg">{s.code}</span>
                      <span className="font-mono text-[10px] text-fg-mute">{s.count}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 text-[10px]">
                      <span className="font-mono uppercase text-fg-mute">{s.vendors.join("/")}</span>
                      <span className="flex items-center gap-1.5">
                        {s.online > 0 ? <span className="text-mint"> ● {s.online}</span> : null}
                        {s.alerting > 0 ? <span className="text-peach"> ● {s.alerting}</span> : null}
                        {s.offline > 0 ? <span className="text-rose"> ● {s.offline}</span> : null}
                        {s.unknown > 0 ? <span className="text-fg-mute">● {s.unknown}</span> : null}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {t.pickerOverflow > 0 ? (
            <div className="rounded-xl border border-peach/30 bg-peach/8 px-3 py-2.5 text-[11px] text-fg-dim">
              <b className="text-peach">{t.pickerOverflow.toLocaleString()}</b>{" "}
              more sites match your filters. Type a more specific site name above to narrow further (or click into{" "}
              <Link to="/sites" className="text-violet underline">/sites</Link> for the full list).
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function TopologyRightRail({ t }: { t: TopologyPageState }) {
  if (t.selectedSerial) {
    return (
      <DeviceDetailsCard
        serial={t.selectedSerial}
        details={t.selectedDetails}
        loading={t.selectedDetailsLoading}
        error={t.selectedDetailsError}
        onClose={() => t.setSelectedSerial(null)}
        onRefresh={() => void t.deviceDetailsStore.load(t.selectedSerial!, { force: true })}
      />
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <CardTitle>Map summary</CardTitle>
          <CardSub className="mt-1">{t.singleSite} · {t.filtered.length} devices</CardSub>
        </div>
        {t.mode === "l2" && t.linkLayer ? (
          <div className="flex flex-col gap-2 text-xs">
            <SummaryRow icon={<Layers size={12} />} label="L2 nodes" value={t.linkLayer.nodes.length} />
            <SummaryRow icon={<Layers size={12} />} label="L2 links" value={t.linkLayer.links.length} />
            <SummaryRow icon={<Server size={12} />} label="Managed devices" value={t.linkLayer.nodes.filter((n) => n.type === "device").length} />
            <SummaryRow icon={<Wifi size={12} />} label="Discovered hosts" value={t.linkLayer.nodes.filter((n) => n.type !== "device").length} />
          </div>
        ) : t.mode === "mesh" && t.mesh ? (
          <div className="flex flex-col gap-2 text-xs">
            <SummaryRow icon={<Wifi size={12} />} label="Mesh roots" value={t.mesh.roots.length} />
            <SummaryRow icon={<Sparkles size={12} />} label="Mesh leaves" value={Object.keys(t.mesh.parentOf).length} />
            <SummaryRow icon={<Layers size={12} />} label="Max hops" value={Math.max(0, ...Object.values(t.mesh.hopCount))} />
          </div>
        ) : (
          <div className="flex flex-col gap-2 text-xs">
            <SummaryRow icon={<Server size={12} />} label="Devices in site" value={t.filtered.length} />
            <SummaryRow icon={<Wifi size={12} />} label="Vendors total" value={new Set(t.data.map((d) => d.vendor)).size} />
            <SummaryRow icon={<Layers size={12} />} label="Total inventory" value={t.data.length} />
          </div>
        )}
        {t.linkLayerLoading || t.meshLoading ? <PanelSkeleton /> : null}
        {t.mesh && (t.mesh.roots.length > 0 || Object.keys(t.mesh.parentOf).length > 0) ? (
          <CascadeRiskCard mesh={t.mesh} devices={t.filtered} />
        ) : null}
        {t.overflow > 0 ? (
          <div className="rounded-xl border border-peach/30 bg-peach/10 p-3 text-[11px] text-peach">
            <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] opacity-80">Capped</div>
            {t.overflow.toLocaleString()} more devices match. Narrow the filter (vendor / status) to bring them into view.
          </div>
        ) : null}
        <div className="rounded-xl border border-line bg-surface-tint/40 p-3 text-[11px] text-fg-dim">
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">Tip</div>
          {t.mode === "l2"
            ? "Real L2 topology from Meraki's LLDP/CDP discovery — MX on the left, switches in the middle, APs/endpoints on the right. Red animated edges = endpoint is offline. Zoom in for device names; zoom out for a dot-map overview."
            : t.mode === "mesh"
              ? "Animated red edges = parent AP is offline, children are cascade-at-risk. Zoom in to see device names; zoom out for a dot-map overview."
              : "Zoom out to see all devices as colored dots. Drag to rearrange · scroll to zoom · use the mini-map to navigate."}
        </div>
        <Button asChild variant="ghost" size="sm" onClick={() => t.setSiteQuery("")}>
          <span className="cursor-pointer">← Back to all sites</span>
        </Button>
      </CardContent>
    </Card>
  );
}

export function TopologyPage() {
  const animatedRootRef = useUiAutoAnimate();
  const flowHostRef = useRef<HTMLDivElement | null>(null);
  const t = useTopologyPage(flowHostRef);

  if (t.isInitialLoading) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" /><span className="opacity-40">/</span><Skeleton className="h-3 w-16" />
        </div>
        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[720px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-40" /><Skeleton className="h-4 w-[92%]" /><Skeleton className="h-4 w-[84%]" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-20 rounded-full" /><Skeleton className="h-7 w-20 rounded-full" />
            <Skeleton className="h-7 w-20 rounded-full" /><Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3">
            <Skeleton className="h-3 w-10" /><Skeleton className="h-7 w-20 rounded-full" />
            <Skeleton className="h-7 w-20 rounded-full" /><Skeleton className="h-3 w-10" />
            <Skeleton className="h-7 w-20 rounded-full" /><Skeleton className="h-7 w-20 rounded-full" />
            <Skeleton className="h-10 min-w-[220px] flex-1 rounded-xl" /><Skeleton className="ml-auto h-7 w-24 rounded-full" />
          </CardContent>
        </Card>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <PanelSkeleton className="h-[760px]" /><PanelSkeleton className="h-[760px]" />
        </div>
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Topology</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Topology</h1>
          <p className="mt-1.5 max-w-[720px] text-sm text-fg-dim">
            Cross-vendor map. Default view collapses to one node per site; narrow the site filter to a single match to drill into devices and (for wireless) the live mesh hierarchy.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="ok" size="sm" dot>{t.filtered.filter((d) => d.status === "online").length} online</Badge>
          <Badge variant="warn" size="sm" dot>{t.filtered.filter((d) => d.status === "alerting").length} alerting</Badge>
          <Badge variant="err" size="sm" dot>{t.filtered.filter((d) => d.status === "offline").length} offline</Badge>
          <Button size="sm" variant="ghost" onClick={() => void t.load()} disabled={t.status === "loading"}>
            <RefreshCw size={13} />Refresh
          </Button>
        </div>
      </div>

      <NoVendorKeysBanner vendor="either" />

      {t.error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span><b className="font-semibold">Showing fixture data.</b> <span className="text-peach/80">{t.error}</span></span>
        </output>
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">vendor</span>
            {ALL_VENDORS.map((v) => {
              const on = t.vendors.includes(v);
              const n = t.vendorCounts[v] ?? 0;
              return (
                <button key={v} type="button" onClick={() => t.toggleVendor(v)} disabled={n === 0}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors",
                    on ? "border-violet/40 bg-aurora-soft text-violet" : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                    n === 0 && "opacity-40 cursor-not-allowed",
                  )}
                  title={n === 0 ? `No ${v} devices in inventory` : undefined}>
                  {v}<span className="ml-0.5 font-mono text-[9.5px] opacity-70">{n}</span>
                </button>
              );
            })}
          </div>
          <div className="relative min-w-[220px] flex-1">
            <Search size={14} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute" />
            <Input value={t.siteQuery} onChange={(e) => t.setSiteQuery(e.target.value)}
              placeholder="Filter by site name (type a unique slug to drill into one site)…" className="pl-9" />
          </div>
          <div className="ml-auto flex items-center gap-2 font-mono text-xs text-fg-mute">
            {t.mode === "picker" ? (
              <Badge variant="info" size="sm"><Building2 size={11} />{t.sitesInView.length} sites</Badge>
            ) : t.mode === "l2" ? (
              <Badge variant="info" size="sm"><Layers size={11} />L2 {t.showWireless ? "full tree" : "wired only"}</Badge>
            ) : t.mode === "mesh" ? (
              <Badge variant="info" size="sm"><Sparkles size={11} />mesh view</Badge>
            ) : (
              <Badge variant="neutral" size="sm"><Layers size={11} />site view</Badge>
            )}
            <span>{t.filtered.length}/{t.data.length} match{t.overflow > 0 ? ` · capped to ${MAX_NODES_PER_SITE}` : ""}</span>
          </div>
        </CardContent>
      </Card>

      {t.isPickerView ? <TopologyPickerView t={t} /> : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <Card>
            <div ref={flowHostRef} style={{ height: 760 }} className="relative rounded-2xl">
              {t.nodes.length > 0 ? (
                <>
                  <TopologyFlowCanvas
                    nodes={t.nodes} edges={t.styledEdges} nodeTypes={nodeTypes}
                    colorMode={t.themeMode}
                    fitDep={t.mode + ":" + (t.singleSiteSlug ?? "all") + ":" + t.nodes.length}
                    onNodeClick={(_e, n) => {
                      const dev = (n.data as DeviceNodeData | undefined)?.device;
                      if (dev?.serial) { t.setSelectedSerial(dev.serial); void t.deviceDetailsStore.load(dev.serial); }
                      t.setPinnedNodeId(n.id);
                    }}
                    onPaneClick={() => { t.setSelectedSerial(null); t.setPinnedNodeId(null); t.setHoveredEdgeId(null); t.setHoverCard(null); }}
                    onNodeMouseEnter={(e, n) => { t.setHoverNodeId(n.id); t.setHoverCardFromEvent(e, n.id); }}
                    onNodeMouseMove={(e, n) => { if (t.hoverNodeId === n.id) t.setHoverCardFromEvent(e, n.id); }}
                    onNodeMouseLeave={() => { t.setHoverNodeId(null); t.setHoverCard(null); }}
                    onEdgeMouseEnter={(_e, edge) => t.setHoveredEdgeId(edge.id)}
                    onEdgeMouseLeave={() => t.setHoveredEdgeId(null)}
                  />
                  <TopologyCanvasFilters
                    statuses={t.statuses} onToggleStatus={t.toggleStatus}
                    showAccessPoints={t.showWireless}
                    onToggleAccessPoints={() => t.setShowWireless((v) => !v)}
                    showAccessPointsToggle={t.mode === "l2" && t.wirelessCount > 0}
                  />
                  {t.hoverCard && t.hoverCardDevice ? (
                    <TopologyHoverCard device={t.hoverCardDevice} l2Node={t.hoverCardL2Node} x={t.hoverCard.x} y={t.hoverCard.y} />
                  ) : null}
                </>
              ) : (
                <div className="p-5">
                  {t.status === "loading" ? (
                    <div className="grid grid-cols-1 gap-3"><PanelSkeleton /><PanelSkeleton /></div>
                  ) : (
                    <div className="flex h-full items-center justify-center text-sm text-fg-mute">
                      No devices match your filters — widen them above.
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
          <TopologyRightRail t={t} />
        </div>
      )}
    </div>
  );
}

/** Cascade-risk card — surfaces non-online mesh parents that still have
 *  children listed. Helps QA spot sites where "6 dormant" actually
 *  means "6 dormant parents + 30 stranded leaves." */
function CascadeRiskCard({
  mesh,
  devices,
}: {
  mesh: MeshTopology;
  devices: InventoryDevice[];
}) {
  const bySerial = new Map(devices.map((d) => [d.serial, d]));
  const risky = Object.entries(mesh.children)
    .map(([parentSerial, kids]) => {
      const p = bySerial.get(parentSerial);
      if (!p) return null;
      if (p.status === "online") return null;
      return { parent: p, childCount: kids.length };
    })
    .filter(
      (x): x is { parent: InventoryDevice; childCount: number } => x !== null,
    )
    .sort((a, b) => b.childCount - a.childCount)
    .slice(0, 4);

  if (risky.length === 0) {
    return (
      <div className="rounded-xl border border-mint/30 bg-mint/8 p-3 text-[11px] text-mint">
        <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] opacity-80">
          Cascade risk
        </div>
        All mesh parents are online — no cascade risk.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-peach/30 bg-peach/8 p-3 text-[11px]">
      <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-peach">
        Cascade risk
      </div>
      <div className="flex flex-col gap-1">
        {risky.map(({ parent, childCount }) => (
          <div
            key={parent.serial}
            className="flex items-center justify-between gap-2"
          >
            <span className="truncate text-fg-dim">
              <b className="text-fg">{parent.name ?? parent.serial}</b>
              <span className="ml-1 font-mono opacity-70">{parent.status}</span>
            </span>
            <span className="font-mono text-fg">
              {childCount} child{childCount === 1 ? "" : "ren"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Right-rail panel that swaps in when a node is clicked on the L2
 *  canvas. Renders ports for switches, clients for APs/appliances.
 *  Compact layout — the rail is 320px so the port list is intentionally
 *  one-line-per-port. */
function DeviceDetailsCard({
  serial,
  details,
  loading,
  error,
  onClose,
  onRefresh,
}: {
  serial: string;
  details: DeviceDetails | undefined;
  loading: boolean;
  error: string | undefined;
  onClose: () => void;
  onRefresh: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate">
              {details?.name ?? serial}
            </CardTitle>
            <CardSub className="mt-0.5 truncate font-mono text-[11px]">
              {details?.model ?? "—"} · {serial}
            </CardSub>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close device details"
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-fg-mute hover:bg-surface-2 hover:text-fg"
            title="Close (or click empty canvas)"
          >
            ✕
          </button>
        </div>

        {error ? (
          <div className="rounded-lg border border-rose/30 bg-rose/8 p-2 text-[11px] text-rose">
            <b>Failed to load:</b> {error}
          </div>
        ) : null}

        {loading && !details ? <FormSkeleton lines={3} /> : null}

        {details ? (
          <>
            <div className="flex flex-wrap gap-1.5 text-[10px]">
              {details.lanIp ? (
                <Badge variant="neutral" size="sm">
                  LAN {details.lanIp}
                </Badge>
              ) : null}
              {details.firmware ? (
                <Badge variant="neutral" size="sm" title="Firmware">
                  FW {details.firmware}
                </Badge>
              ) : null}
              <Badge variant="info" size="sm">
                {details.productType}
              </Badge>
            </div>

            {details.productType === "switch" && details.ports?.length ? (
              <DevicePortsList ports={details.ports} />
            ) : null}

            {details.clients?.length ? (
              <DeviceClientsList clients={details.clients} />
            ) : details.productType !== "switch" ? (
              <div className="rounded-lg border border-line bg-surface-tint/40 p-3 text-center text-[11px] text-fg-mute">
                No connected clients in the last hour.
              </div>
            ) : null}
          </>
        ) : null}

        <div className="flex items-center justify-between gap-2 border-t border-line pt-2">
          <span className="font-mono text-[10px] text-fg-mute">
            click pane to clear
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw size={12} />
            Refresh
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Compact one-line-per-port renderer. Color-coded by status. */
function DevicePortsList({
  ports,
}: {
  ports: NonNullable<DeviceDetails["ports"]>;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between px-1 text-[10px] font-mono uppercase tracking-[0.12em] text-fg-mute">
        <span>Ports</span>
        <span>
          {
            ports.filter((p) => (p.status ?? "").toLowerCase() === "connected")
              .length
          }
          /{ports.length} up
        </span>
      </div>
      <div className="max-h-[360px] overflow-y-auto rounded-lg border border-line">
        {ports.map((p, pi) => {
          const up = (p.status ?? "").toLowerCase() === "connected";
          const enabled = p.enabled !== false;
          return (
            <div
              key={p.portId ?? `port-${pi}`}
              className={cn(
                "flex items-center gap-2 border-b border-line/60 px-2 py-1 text-[11px] last:border-b-0",
                !enabled && "opacity-40",
              )}
            >
              <span className="w-8 shrink-0 font-mono text-fg">
                {p.portId ?? "—"}
              </span>
              <span
                className={cn(
                  "h-2 w-2 shrink-0 rounded-full",
                  up ? "bg-mint" : enabled ? "bg-fg-mute" : "bg-line",
                )}
                title={p.status ?? (enabled ? "disconnected" : "disabled")}
              />
              <span className="min-w-0 flex-1 truncate text-fg-dim">
                {p.name ??
                  (p.type === "trunk"
                    ? `trunk · vlans ${p.allowedVlans ?? "?"}`
                    : `access · vlan ${p.vlan ?? "?"}`)}
              </span>
              {typeof p.clientCount === "number" && p.clientCount > 0 ? (
                <span className="shrink-0 font-mono text-[10px] text-violet">
                  {p.clientCount} cli
                </span>
              ) : null}
              {p.speed ? (
                <span className="shrink-0 font-mono text-[10px] text-fg-mute">
                  {p.speed}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Client list — wired or wireless, mixed for APs/appliances. Caps at
 *  the first 50 rows so the panel stays usable on a busy SSID. */
function DeviceClientsList({
  clients,
}: {
  clients: NonNullable<DeviceDetails["clients"]>;
}) {
  const visible = clients.slice(0, 50);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between px-1 text-[10px] font-mono uppercase tracking-[0.12em] text-fg-mute">
        <span>Clients (last 1h)</span>
        <span>{clients.length}</span>
      </div>
      <div className="max-h-[280px] overflow-y-auto rounded-lg border border-line">
        {visible.map((c, ci) => (
          <div
            key={c.id ?? c.mac ?? `client-${ci}`}
            className="flex items-center gap-2 border-b border-line/60 px-2 py-1 text-[11px] last:border-b-0"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-fg">
                {c.description ?? c.mac ?? "—"}
              </div>
              <div className="font-mono text-[9.5px] text-fg-mute">
                {c.ip ?? "—"}
                {c.ssid
                  ? ` · ${c.ssid}`
                  : c.switchport
                    ? ` · port ${c.switchport}`
                    : ""}
                {c.vlan ? ` · vlan ${c.vlan}` : ""}
              </div>
            </div>
            {c.manufacturer ? (
              <span className="shrink-0 font-mono text-[9.5px] text-fg-mute">
                {c.manufacturer}
              </span>
            ) : null}
          </div>
        ))}
        {clients.length > visible.length ? (
          <div className="px-2 py-1.5 text-center text-[10px] text-fg-mute">
            … {clients.length - visible.length} more
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SummaryRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-line bg-surface px-3 py-2">
      <div className="flex items-center gap-2 text-fg-dim">
        <span className="text-violet">{icon}</span>
        {label}
      </div>
      <div className="font-mono text-fg">{value.toLocaleString()}</div>
    </div>
  );
}

function TopologyCanvasFilters({
  statuses,
  onToggleStatus,
  showAccessPoints,
  onToggleAccessPoints,
  showAccessPointsToggle,
}: {
  statuses: DeviceStatus[];
  onToggleStatus: (status: DeviceStatus) => void;
  showAccessPoints: boolean;
  onToggleAccessPoints: () => void;
  showAccessPointsToggle: boolean;
}) {
  return (
    <div className="pointer-events-auto absolute right-3 top-3 z-20 w-[280px] rounded-xl border border-line/85 bg-bg-0/92 p-2.5 shadow-[0_12px_28px_-20px_rgba(0,0,0,0.8)] backdrop-blur">
      <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.12em] text-fg-mute">
        Filters
      </div>
      <div className="grid grid-cols-2 gap-1.5">
        {ALL_STATUSES.map((s) => {
          const active = statuses.includes(s);
          return (
            <button
              key={s}
              type="button"
              onClick={() => onToggleStatus(s)}
              className={cn(
                "inline-flex items-center justify-center rounded-md border px-2 py-1 text-[11px] font-semibold capitalize transition-colors",
                active
                  ? s === "online"
                    ? "border-mint/50 bg-mint/12 text-mint"
                    : s === "alerting"
                      ? "border-peach/50 bg-peach/12 text-peach"
                      : s === "offline"
                        ? "border-rose/50 bg-rose/12 text-rose"
                        : "border-violet/45 bg-aurora-soft text-violet"
                  : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
              )}
            >
              {s}
            </button>
          );
        })}
      </div>
      {showAccessPointsToggle ? (
        <button
          type="button"
          onClick={onToggleAccessPoints}
          className={cn(
            "mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-semibold transition-colors",
            showAccessPoints
              ? "border-violet/45 bg-aurora-soft text-violet"
              : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
          )}
          title={
            showAccessPoints
              ? "Hide access points from topology"
              : "Show access points in topology"
          }
        >
          <Wifi size={12} />
          Access Points
        </button>
      ) : null}
    </div>
  );
}

function TopologyHoverCard({
  device,
  l2Node,
  x,
  y,
}: {
  device: InventoryDevice;
  l2Node: LinkLayerNode | undefined;
  x: number;
  y: number;
}) {
  const deviceClass = l2Node?.productType ?? classOf(device);
  const ip = l2Node?.ipv4 ?? device.ipAddress ?? "—";
  const mac = l2Node?.mac ?? device.mac ?? "—";
  const tags = device.tags?.length ? device.tags.join(", ") : "—";
  const lastSeen = formatLastSeen(l2Node?.lastReported);

  return (
    <div
      className="pointer-events-none absolute z-30 w-[300px] rounded-xl border border-line/90 bg-bg-0/95 p-3 shadow-[0_20px_36px_-24px_rgba(0,0,0,0.9)] backdrop-blur"
      style={{ left: x, top: y }}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12px] font-semibold text-fg">
            {device.name ?? "(unnamed device)"}
          </div>
          <div className="truncate font-mono text-[10px] text-fg-mute">
            {device.serial}
          </div>
        </div>
        <Badge
          size="sm"
          variant={
            device.status === "online"
              ? "ok"
              : device.status === "alerting"
                ? "warn"
                : device.status === "offline"
                  ? "err"
                  : "neutral"
          }
          dot
        >
          {device.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 gap-1.5 text-[10.5px] text-fg-dim">
        <HoverDetailRow label="Model" value={device.model ?? "—"} mono />
        <HoverDetailRow label="Vendor" value={device.vendor} />
        <HoverDetailRow label="Class" value={String(deviceClass)} />
        <HoverDetailRow label="LAN/Public IP" value={ip} mono />
        <HoverDetailRow label="MAC" value={mac} mono />
        <HoverDetailRow label="Last seen" value={lastSeen} />
        <HoverDetailRow label="Tags" value={tags} />
      </div>
    </div>
  );
}

function HoverDetailRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-line/70 bg-surface/55 px-2 py-1">
      <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-fg-mute">
        {label}
      </span>
      <span
        className={cn(
          "max-w-[58%] truncate text-right text-fg",
          mono && "font-mono",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function formatLastSeen(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const delta = Date.now() - ts;
  if (delta < 60_000) return "just now";
  if (delta < 3_600_000) {
    const mins = Math.floor(delta / 60_000);
    return `${mins} min${mins === 1 ? "" : "s"} ago`;
  }
  if (delta < 86_400_000) {
    const hours = Math.floor(delta / 3_600_000);
    return `${hours} h ago`;
  }
  const days = Math.floor(delta / 86_400_000);
  return `${days} d ago`;
}
